// The boundary for task JSON: the generated client types the rows, but not their JSON columns or
// a timeline entry's `detail`. The API serialises its own validated types, so this checks the
// outline and the enums and refuses what it does not know, as catalog/parse.ts does.
import { parseOrchestration, parseWaitingOn } from "./subtasks/api";
import type { Schemas } from "../api/client";
import { parseGraph, parseRequirements } from "../catalog/parse";
import {
  type ActiveStep,
  GROUP_STATUSES,
  type GroupRepository,
  type GroupRequirements,
  type Isolation,
  REQUIREMENT_STATES,
  SOURCES,
  type StepKind,
  TASK_STATUSES,
  type Task,
  type TaskDetail,
  type TaskGroup,
  type TaskStep,
  type TimelineEntry,
} from "./model";

type Json = Record<string, unknown>;
const isObject = (x: unknown): x is Json => typeof x === "object" && x !== null && !Array.isArray(x);

function malformed(what: string): never {
  throw new Error(`ocs-api sent a malformed ${what}`);
}

function oneOf<T extends string>(values: readonly T[], x: unknown, what: string): T {
  return values.find((v) => v === x) ?? malformed(what);
}

const STEP_KINDS = ["prompt", "approval", "orchestrate", "end"] as const satisfies readonly StepKind[];
const ISOLATIONS = ["container", "vm"] as const satisfies readonly Isolation[];

const str = (x: unknown): string => (typeof x === "string" ? x : "");
const num = (x: unknown): number => (typeof x === "number" ? x : 0);
const optStr = (x: unknown): string | null => (typeof x === "string" ? x : null);

function stringMap(x: unknown): Record<string, string> {
  if (!isObject(x)) return {};
  return Object.fromEntries(Object.entries(x).filter((e): e is [string, string] => typeof e[1] === "string"));
}

function countMap(x: unknown): Record<string, number> {
  if (!isObject(x)) return {};
  return Object.fromEntries(Object.entries(x).filter((e): e is [string, number] => typeof e[1] === "number"));
}

function parseRepositories(x: unknown): GroupRepository[] {
  if (!Array.isArray(x)) return [];
  return x.filter(isObject).map((r) => ({ repo: str(r.repo), default_branch: optStr(r.default_branch) }));
}

// S56's fields (repositories, task links, step errors) are read loosely, so the pages work
// against an API, and a generated schema, from before them.
const loose = (x: object): Json => x as Json;

/** `bindings` as stored: requirement index → credential id or "platform"; anything else dropped. */
function parseBindings(x: unknown): Record<string, string> {
  if (!isObject(x)) return {};
  return Object.fromEntries(Object.entries(x).filter((e): e is [string, string] => typeof e[1] === "string" && /^\d+$/.test(e[0])));
}

export function parseGroup(g: Schemas["TaskGroupDto"]): TaskGroup {
  return {
    ...g,
    repositories: parseRepositories(loose(g).repositories),
    status: oneOf(GROUP_STATUSES, g.status, "task group status"),
    source: oneOf(SOURCES, g.source, "task group source"),
    isolation: oneOf(ISOLATIONS, g.isolation, "task group isolation"),
    source_config: isObject(g.source_config) ? g.source_config : {},
    bindings: parseBindings(g.bindings),
    requires_vpn: g.requires_vpn === true,
    network_list_id: optStr(loose(g).network_list_id),
    share_vm_network: loose(g).share_vm_network === true,
  };
}

function parseActiveStep(x: unknown): ActiveStep | null {
  if (!isObject(x)) return null;
  const node = str(x.node);
  const visit = num(x.visit);
  if (x.kind === "prompt") return { kind: "prompt", node, visit, run: str(x.run) };
  if (x.kind === "approval") return { kind: "approval", node, visit, question: str(x.question) };
  if (x.kind === "orchestrate") return { kind: "orchestrate", node, visit };
  // A step kind this build does not know yet (a newer server): the task page still loads,
  // it just shows no in-flight step.
  return null;
}

export function parseGroupRequirements(r: Schemas["TaskGroupRequirementsDto"]): GroupRequirements {
  const declared = parseRequirements(r.requirements.map((s) => s.requirement));
  return {
    workflow_version_id: r.workflow_version_id,
    satisfied: r.satisfied,
    requirements: r.requirements.map((s, i) => ({
      index: s.index,
      requirement: declared[i]!,
      state: oneOf(REQUIREMENT_STATES, s.state, "requirement state"),
      satisfied: s.satisfied,
      binding: s.binding ?? null,
    })),
  };
}

export function parseTask(t: Schemas["TaskDto"]): Task {
  return {
    ...t,
    parent_task_id: optStr(loose(t).parent_task_id),
    child_count: num(loose(t).child_count),
    status: oneOf(TASK_STATUSES, t.status, "task status"),
    visits: countMap(t.visits),
    active_step: parseActiveStep(t.active_step),
    waiting_on: parseWaitingOn(loose(t).waiting_on),
    ...parseOrchestration(t),
  };
}

export function parseStep(s: Schemas["TaskStepDto"]): TaskStep {
  return { ...s, kind: oneOf(STEP_KINDS, s.kind, "step kind"), outputs: stringMap(s.outputs), error: optStr(loose(s).error) };
}

export function parseTaskDetail(d: Schemas["TaskDetailDto"]): TaskDetail {
  return {
    task: parseTask(d.task),
    steps: d.steps.map(parseStep),
    workflow_version_id: d.workflow_version_id,
    definition: parseGraph(d.definition),
    current_node: d.current_node ?? null,
  };
}

/** An entry of a kind this page does not know yet is left out rather than failing the page. */
export function parseTimelineEntry(e: Schemas["TimelineEntryDto"]): TimelineEntry | null {
  const d: Json = isObject(e.detail) ? e.detail : {};
  const at = e.at;
  const node = e.node_id ?? "";
  if (e.kind.startsWith("task_")) {
    return { kind: "status", at, status: oneOf(TASK_STATUSES, e.kind.slice(5), "timeline status"), reason: optStr(d.reason) };
  }
  const ref = { at, node, nodeName: str(d.node_name) || node, visit: num(d.visit) };
  switch (e.kind) {
    case "step_started":
      return { ...ref, kind: "step_started", stepKind: oneOf(STEP_KINDS, d.step_kind, "step kind"), runId: optStr(d.run_id) };
    case "step_finished":
      return {
        ...ref,
        kind: "step_finished",
        stepKind: oneOf(STEP_KINDS, d.step_kind, "step kind"),
        runId: optStr(d.run_id),
        outcome: optStr(d.outcome),
        outputs: stringMap(d.outputs),
        error: optStr(d.error),
        costUsd: num(d.cost_usd),
        inputTokens: num(d.input_tokens),
        outputTokens: num(d.output_tokens),
      };
    case "approval_asked":
      return {
        kind: "approval_asked",
        at,
        node,
        nodeName: ref.nodeName,
        question: str(d.question),
        options: Array.isArray(d.options) ? d.options.filter((o): o is string => typeof o === "string") : [],
      };
    case "approval_answered":
      return { kind: "approval_answered", at, node, answer: str(d.answer) };
    default:
      return null;
  }
}

export function parseTimeline(entries: Schemas["TimelineEntryDto"][]): TimelineEntry[] {
  return entries.flatMap((e) => parseTimelineEntry(e) ?? []);
}
